import { useEffect, useState } from 'react';
import {
 Box,
 Button,
 Checkbox,
 Flex,
 FormControl,
 FormLabel,
 Input,
 Modal,
 ModalBody,
 ModalCloseButton,
 ModalContent,
 ModalFooter,
 ModalHeader,
 ModalOverlay,
 Select,
 Stack,
 Switch,
 Text,
 useToast,
} from '@chakra-ui/react';
import { FiLock, FiUnlock } from 'react-icons/fi';
import {
 createSchedule,
 updateSchedule,
 listCompanies,
 getConsultantCandidates,
} from '../../services/scheduleService';

export default function ScheduleFormModal({ isOpen, onClose, schedule, onSaved }) {
 const toast = useToast();
 const isEdit = !!schedule;

 const [name, setName] = useState('');
 const [idCompany, setIdCompany] = useState('');
 const [companies, setCompanies] = useState([]);
 const [consultants, setConsultants] = useState([]);
 const [selectedConsultants, setSelectedConsultants] = useState([]);
 const [editLocked, setEditLocked] = useState(false);
 const [saving, setSaving] = useState(false);

 useEffect(() => {
  if (!isOpen) return;
  listCompanies()
   .then((data) => setCompanies(Array.isArray(data) ? data : []))
   .catch(() => setCompanies([]));
 }, [isOpen]);

 useEffect(() => {
  if (!isOpen) return;
  if (isEdit) {
   setName(schedule.name || '');
   setIdCompany(String(schedule.id_company || ''));
   setSelectedConsultants((schedule.consultants || []).map((c) => c.id_user));
   setEditLocked(!!schedule.edit_locked);
  } else {
   setName('');
   setIdCompany('');
   setSelectedConsultants([]);
   setEditLocked(false);
  }
 }, [isOpen, schedule, isEdit]);

 useEffect(() => {
  if (!idCompany) {
   setConsultants([]);
   return;
  }
  getConsultantCandidates(idCompany)
   .then((data) => setConsultants(Array.isArray(data) ? data : []))
   .catch(() => setConsultants([]));
 }, [idCompany]);

 function toggleConsultant(id) {
  setSelectedConsultants((prev) =>
   prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
  );
 }

 async function handleSubmit() {
  if (!name.trim()) {
   toast({ title: 'Informe o nome do cronograma', status: 'warning' });
   return;
  }
  if (!isEdit && !idCompany) {
   toast({ title: 'Selecione a empresa', status: 'warning' });
   return;
  }
  setSaving(true);
  try {
   const payload = {
    name: name.trim(),
    consultant_ids: selectedConsultants,
    edit_locked: editLocked,
   };
   if (isEdit) {
    await updateSchedule(schedule.id_schedule, payload);
   } else {
    await createSchedule({ ...payload, id_company: Number(idCompany) });
   }
   toast({ title: 'Cronograma salvo', status: 'success' });
   onSaved?.();
  } catch (err) {
   toast({ title: 'Erro ao salvar cronograma', status: 'error' });
  } finally {
   setSaving(false);
  }
 }

 return (
  <Modal isOpen={isOpen} onClose={onClose} size='2xl' scrollBehavior='inside'>
   <ModalOverlay />
   <ModalContent borderRadius='20px' overflow='hidden' maxW='1100px' maxH='90vh'>
    <ModalHeader pb='8px'>
     <Box>
      <Text fontSize='20px' fontWeight='800' color='gray.800'>
       {isEdit ? 'Editar cronograma' : 'Novo cronograma'}
      </Text>
      <Text fontSize='sm' color='gray.500' mt='4px'>
       Defina os dados principais e os consultores envolvidos neste cronograma.
      </Text>
     </Box>
    </ModalHeader>
    <ModalCloseButton top='16px' right='16px' />
    <ModalBody px='24px' py='8px' maxH='74vh' overflowY='auto'>
     <Stack spacing='20px'>
      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl isRequired>
        <FormLabel fontSize='sm' fontWeight='700'>Nome do cronograma</FormLabel>
        <Input
         value={name}
         onChange={(e) => setName(e.target.value)}
         placeholder='Ex.: Diagnóstico ESG - Q3'
         size='md'
        />
       </FormControl>
      </Box>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl isRequired>
        <FormLabel fontSize='sm' fontWeight='700'>Empresa</FormLabel>
        <Select
         value={idCompany}
         onChange={(e) => setIdCompany(e.target.value)}
         placeholder='Selecione a empresa'
         isDisabled={isEdit}
         size='md'
        >
         {companies.map((c) => (
          <option key={c.id_company} value={c.id_company}>
           {c.fantasyName || c.name}
          </option>
         ))}
        </Select>
        <Text fontSize='sm' color='gray.500' mt='6px'>
         A empresa define o contexto do cronograma e as atividades relacionadas.
        </Text>
       </FormControl>
      </Box>

      <Box bg='gray.50' border='1px solid #E7E9EE' borderRadius='14px' p='16px'>
       <FormControl>
        <FormLabel fontSize='sm' fontWeight='700'>Consultor(es) responsável(is)</FormLabel>
        {!idCompany ? (
         <Text fontSize='sm' color='gray.500'>
          Selecione a empresa para listar os consultores.
         </Text>
        ) : consultants.length === 0 ? (
         <Text fontSize='sm' color='gray.500'>
          Nenhum consultor interno vinculado a esta empresa.
         </Text>
        ) : (
         <Stack maxH='220px' overflowY='auto' spacing='10px' pr='2px'>
          {consultants.map((c) => (
           <Flex
            key={c.id_user}
            align='center'
            justify='space-between'
            gap='8px'
            p='10px 12px'
            border={selectedConsultants.includes(c.id_user) ? '2px solid #2D70EE' : '1px solid #E7E9EE'}
            borderRadius='10px'
            bg={selectedConsultants.includes(c.id_user) ? 'blue.50' : '#fff'}
            boxShadow={selectedConsultants.includes(c.id_user) ? '0 0 0 1px #2D70EE' : 'none'}
           >
            <Box>
             <Text fontSize='sm' fontWeight='600' color='gray.800'>
              {c.username}
             </Text>
             <Text fontSize='xs' color='gray.500'>
              {c.email}
             </Text>
            </Box>
            <Checkbox
             isChecked={selectedConsultants.includes(c.id_user)}
             onChange={() => toggleConsultant(c.id_user)}
             colorScheme='blue'
             size='lg'
             borderColor='#2D70EE'
            />
           </Flex>
          ))}
         </Stack>
        )}
       </FormControl>
      </Box>

      <Box
       as='button'
       type='button'
       onClick={() => setEditLocked((v) => !v)}
       textAlign='left'
       w='100%'
       borderRadius='16px'
       p='18px'
       border='2px solid'
       borderColor={editLocked ? '#E1932B' : '#E7E9EE'}
       bg={
        editLocked
         ? 'linear-gradient(135deg, #FFF7EA 0%, #FFFBF3 100%)'
         : '#fff'
       }
       boxShadow={editLocked ? '0 6px 18px rgba(225,147,43,0.18)' : 'none'}
       transition='all 0.18s ease'
       _hover={{ borderColor: editLocked ? '#C97C18' : '#CBD5E0' }}
       _focusVisible={{ outline: 'none', boxShadow: '0 0 0 3px rgba(45,112,238,0.35)' }}
      >
       <Flex align='center' gap='16px'>
        <Flex
         align='center'
         justify='center'
         boxSize='46px'
         borderRadius='12px'
         flexShrink={0}
         bg={editLocked ? '#F6C778' : '#EDF0F3'}
         color={editLocked ? '#7A4B08' : '#8A929C'}
         transition='all 0.18s ease'
        >
         <Box as={editLocked ? FiLock : FiUnlock} boxSize='22px' />
        </Flex>
        <Box flex='1' minW={0}>
         <Flex align='center' gap='8px' flexWrap='wrap'>
          <Text fontSize='15px' fontWeight='800' color='gray.800'>
           Bloqueia edição
          </Text>
          {editLocked && (
           <Box
            bg='#E1932B'
            color='#fff'
            px='8px'
            py='2px'
            borderRadius='999px'
            fontSize='10px'
            fontWeight='800'
            letterSpacing='0.03em'
            textTransform='uppercase'
           >
            Travado
           </Box>
          )}
         </Flex>
         <Text fontSize='xs' color='gray.500' mt='4px'>
          Com a trava ativa, apenas usuários internos podem editar ou excluir
          este cronograma e suas atividades. A empresa fica somente com
          visualização.
         </Text>
        </Box>
        <Switch
         isChecked={editLocked}
         onChange={(e) => setEditLocked(e.target.checked)}
         colorScheme='orange'
         size='lg'
         pointerEvents='none'
         flexShrink={0}
        />
       </Flex>
      </Box>
     </Stack>
    </ModalBody>
    <ModalFooter px='24px' py='16px' borderTop='1px solid #EEF1F4'>
     <Button variant='ghost' mr='12px' onClick={onClose}>
      Cancelar
     </Button>
     <Button variant='primary' onClick={handleSubmit} isLoading={saving}>
      Salvar
     </Button>
    </ModalFooter>
   </ModalContent>
  </Modal>
 );
}
